import {useState, useEffect} from 'react';

import {CardWidget} from 'shells/card/card-widget/card-widget';
import {FormField} from 'shells/form/form-field';
import {Stack} from 'shells/layout/stack/stack';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {CharacterLimitedTextfield} from 'shells/textfield/character-limited-textfield';
import {TextFieldDebounced} from 'shells/textfield/textfield-debounced';

import type {
    ChatSettingsIntakeFormTypeEnum,
    ChatSettingsIntakeFormFieldsEnum,
} from 'nutshell-graphql-types';
import type {
    ChatActionsFragment_chat as ChatActionsFragment,
    ChatSettingsActionsInput,
    ChatSettingsLookAndFeelInput,
    ChatSettingsIntakeFormInput,
} from 'nutshell-graphql-types';

import {AUTO_RESOLVE_ID} from '../../../../core/form/fields/consts';

import {ChatLeadsLink} from '../config-options/chat-leads-link';

import {ChatCardCallout} from './chat-card-callout';
import {CollectVisitorInfoOptions} from './collect-visitor-info-options';
import {SAVE_STATE_KEYS, SAVE_DELAY, CHAT_WIDGET, MAX_LABEL_CHARACTER_LIMIT} from './consts';
import type {SaveStateKey, WidgetType} from './consts';
import {DisclaimerSection} from './disclaimer-section';
import {getStatusBadge} from './helpers';
import {LeadCreationToggleSection} from './lead-creation-toggle-section';
import {ToggleSection} from './toggle-section';
import {useDebouncedSaveField} from './use-debounced-save-field';

interface Props {
    actionSettings: ChatActionsFragment;
    isLoadingChatSettings: boolean;
    openWidget: WidgetType | null | undefined;
    setOpenWidget: (value?: WidgetType | null | undefined) => void;
    isDisclaimerEnabled: boolean;
    setIsDisclaimerEnabled: (value: boolean) => void;
    disclaimerMessage: string;
    handleDisclaimerSave: (htmlValue: string) => Promise<any>;
    allowThreadsWithoutContact: boolean;
    setAllowThreadsWithoutContact: (value: boolean) => void;
    intakeFormType: ChatSettingsIntakeFormTypeEnum;
    setIntakeFormType: (value: ChatSettingsIntakeFormTypeEnum) => void;
    intakeFormFields: Array<ChatSettingsIntakeFormFieldsEnum>;
    setIntakeFormFields: (value: Array<ChatSettingsIntakeFormFieldsEnum>) => void;
    openingMessage: string;
    toastState: SavedSettingToastState;
    savingToastKey: SaveStateKey | null | undefined;
    handleActionSave: (
        updates: ChatSettingsActionsInput,
        saveStateKey: SaveStateKey
    ) => Promise<any>;
    handleLookAndFeelSave: (
        updates: ChatSettingsLookAndFeelInput,
        saveStateKey: SaveStateKey
    ) => Promise<any>;
    handleIntakeFormSave: (
        updates: ChatSettingsIntakeFormInput,
        saveStateKey: SaveStateKey
    ) => Promise<any>;
    cardRef?: (arg1?: HTMLDivElement | null | undefined) => void;
}

export function ChatCard(props: Props) {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
    const [isCreateLeadEnabled, setIsCreateLeadEnabled] = useState<boolean>(
        props.actionSettings.shouldCreateLeads
    );

    const savedLabel = props.actionSettings.label || 'Chat';
    const savedOpeningMessage = props.openingMessage;
    const savedPipeline = props.actionSettings.shouldCreateLeadsPipelineId || AUTO_RESOLVE_ID;

    const [pipeline, setPipeline] = useState<string>(savedPipeline);

    const label = useDebouncedSaveField({
        savedValue: savedLabel,
        handleSave: (newLabel) =>
            props.handleActionSave({chat: {label: newLabel}}, SAVE_STATE_KEYS.CHAT_LABEL),
        validation: {isRequired: true, maxLength: MAX_LABEL_CHARACTER_LIMIT},
    });

    const openingMessage = useDebouncedSaveField({
        savedValue: savedOpeningMessage,
        handleSave: (newMessage) =>
            props.handleLookAndFeelSave(
                {conversationStarter: newMessage},
                SAVE_STATE_KEYS.CHAT_OPENING_MESSAGE
            ),
        validation: {isRequired: true},
    });

    useEffect(() => {
        // If openWidget's value changes, collapse the widget
        if (props.openWidget !== CHAT_WIDGET) {
            setIsCollapsed(true);
        }
    }, [props.openWidget]);

    useEffect(() => {
        setPipeline(savedPipeline);
    }, [savedPipeline]);

    return (
        <CardWidget
            cardRef={props.cardRef}
            title='Chat'
            hasBorder={true}
            collapsibleProps={{
                isCollapsed,
                onToggle: () => {
                    setIsCollapsed(!isCollapsed);
                    props.setOpenWidget(isCollapsed ? CHAT_WIDGET : null);
                },
            }}
            type='CHAT'
            secondaryTitle={getStatusBadge(props.actionSettings.isConfigured)}
            rowsHavePadding={false}
        >
            <div className='my-16 mx-12'>
                <Stack spacing={24}>
                    <ChatCardCallout />
                    <FormField
                        innerId='action-label'
                        label='Action label'
                        saveToastState={
                            props.savingToastKey === SAVE_STATE_KEYS.CHAT_LABEL
                                ? props.toastState
                                : 'initial'
                        }
                        noMargin={true}
                    >
                        <CharacterLimitedTextfield
                            placeholder='Chat'
                            maxLength={MAX_LABEL_CHARACTER_LIMIT}
                            value={label.value}
                            onChange={label.onChange}
                            onBlur={label.onBlur}
                            isDebouncedSave={true}
                            debouncedSaveDelay={SAVE_DELAY}
                        />
                    </FormField>
                    <FormField
                        innerId='opening-message'
                        label='Opening message'
                        saveToastState={
                            props.savingToastKey === SAVE_STATE_KEYS.CHAT_OPENING_MESSAGE
                                ? props.toastState
                                : 'initial'
                        }
                        noMargin={true}
                    >
                        <TextFieldDebounced
                            placeholder='Hello! How can we help you?'
                            value={openingMessage.value}
                            onChange={openingMessage.onChange}
                            onBlur={openingMessage.onBlur}
                            delay={SAVE_DELAY}
                        />
                    </FormField>
                    <ToggleSection
                        labelContent='Collect visitor information'
                        description='Collect the visitor’s contact information after the first chatbot reply'
                        isOn={!props.allowThreadsWithoutContact}
                        state={
                            props.savingToastKey === SAVE_STATE_KEYS.ALLOW_THREADS_WITHOUT_CONTACT
                                ? props.toastState
                                : 'initial'
                        }
                        onChange={() => {
                            props.setAllowThreadsWithoutContact(!props.allowThreadsWithoutContact);
                            props.handleIntakeFormSave(
                                {
                                    allowThreadsWithoutContact: !props.allowThreadsWithoutContact,
                                },
                                SAVE_STATE_KEYS.ALLOW_THREADS_WITHOUT_CONTACT
                            );
                        }}
                    >
                        {!props.allowThreadsWithoutContact && (
                            <CollectVisitorInfoOptions
                                intakeFormType={props.intakeFormType}
                                setIntakeFormType={props.setIntakeFormType}
                                intakeFormFields={props.intakeFormFields}
                                setIntakeFormFields={props.setIntakeFormFields}
                                handleSave={props.handleIntakeFormSave}
                                toastState={props.toastState}
                                savingToastKey={props.savingToastKey}
                            />
                        )}
                    </ToggleSection>
                    <LeadCreationToggleSection
                        labelContent='Create a lead for each inbound chat'
                        description='If a lead isn’t created through other actions, we will create one
                        automatically after one hour of chat inactivity'
                        isOn={isCreateLeadEnabled}
                        onToggle={() => {
                            setIsCreateLeadEnabled(!isCreateLeadEnabled);
                            props.handleActionSave(
                                {chat: {shouldCreateLeads: !isCreateLeadEnabled}},
                                SAVE_STATE_KEYS.CHAT_SHOULD_CREATE_LEADS
                            );
                        }}
                        toastState={props.toastState}
                        savingToastKey={props.savingToastKey}
                        toggleSaveKey={SAVE_STATE_KEYS.CHAT_SHOULD_CREATE_LEADS}
                        pipelineSaveKey={SAVE_STATE_KEYS.CHAT_LEADS_PIPELINE_ID}
                        pipeline={pipeline}
                        onSelectPipeline={(newPipeline) => {
                            setPipeline(newPipeline);
                            props.handleActionSave(
                                {
                                    chat: {
                                        shouldCreateLeadsPipelineId:
                                            newPipeline === AUTO_RESOLVE_ID ? null : newPipeline,
                                    },
                                },
                                SAVE_STATE_KEYS.CHAT_LEADS_PIPELINE_ID
                            );
                        }}
                        showPipeline={isCreateLeadEnabled && !props.isLoadingChatSettings}
                        pipelineNoMargin={true}
                        isDisabled={props.allowThreadsWithoutContact}
                    >
                        <ChatLeadsLink />
                    </LeadCreationToggleSection>
                    <DisclaimerSection
                        // @ts-expect-error
                        savingToastKey={props.savingToastKey}
                        toastState={props.toastState}
                        isLoading={props.isLoadingChatSettings}
                        isDisclaimerEnabled={props.isDisclaimerEnabled}
                        setIsDisclaimerEnabled={props.setIsDisclaimerEnabled}
                        disclaimerMessage={props.disclaimerMessage}
                        // @ts-expect-error
                        handleSave={props.handleLookAndFeelSave}
                        handleDisclaimerSave={props.handleDisclaimerSave}
                    />
                </Stack>
            </div>
        </CardWidget>
    );
}
